import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RegisterPage from './RegisterPage';

export default function LoginPage() {
  const { signIn } = useAuth();
  const prefill = localStorage.getItem('rfid_wms_prefill_email') || '';
  const impersonateOrg = (() => { try { return JSON.parse(localStorage.getItem('rfid_wms_impersonate_org') || 'null'); } catch { return null; } })();
  const [email,    setEmail]    = useState(prefill);
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [showReg,  setShowReg]  = useState(false);

  // Read company name cached by Header after last login
  const platformName = localStorage.getItem('rfid_wms_platform_name') || localStorage.getItem('rfid_wms_system_name') || 'RFID WMS';
  const [brandLogo, setBrandLogo] = useState(localStorage.getItem('rfid_wms_brand_logo') || '');

  useEffect(() => {
    // Load logo from Supabase directly — no auth needed (platform_settings is public read)
    const loadBranding = async () => {
      try {
        const { supabase } = await import('../../supabaseClient');
        const { data } = await supabase
          .from('platform_settings')
          .select('key, value')
          .in('key', ['brand_logo', 'brand_favicon']);
        if (data) {
          const logo    = data.find(r => r.key === 'brand_logo');
          const favicon = data.find(r => r.key === 'brand_favicon');
          if (logo?.value)    { setBrandLogo(logo.value); localStorage.setItem('rfid_wms_brand_logo', logo.value); }
          if (favicon?.value) {
            let link = document.querySelector("link[rel*='icon']");
            if (!link) { link = document.createElement('link'); link.rel = 'shortcut icon'; document.head.appendChild(link); }
            link.href = favicon.value;
          }
        }
      } catch(e) {}
    };
    loadBranding();
  }, []);

  if (showReg) return <RegisterPage onBackToLogin={() => setShowReg(false)} />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError('');
    // Clean up impersonation flags on successful login
    localStorage.removeItem('rfid_wms_prefill_email');
    localStorage.removeItem('rfid_wms_impersonate_org');
    const { error: err } = await signIn(email, password);
    if (err) setError(err.message);
    setLoading(false);
  };

  return (
    <div style={{ height:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#060b10' }}>
      {/* Background grid */}
      <div style={{ position:'absolute', inset:0, opacity:0.03, backgroundImage:'linear-gradient(#00e5a0 1px,transparent 1px),linear-gradient(90deg,#00e5a0 1px,transparent 1px)', backgroundSize:'40px 40px' }}/>

      <div style={{ position:'relative', width:'100%', maxWidth:400, padding:20 }}>
        {/* Impersonation banner */}
        {impersonateOrg && (
          <div style={{ background:'#f5a62318', border:'1px solid #f5a62340', borderRadius:10, padding:'10px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ fontSize:16 }}>🔑</div>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:'#f5a623' }}>Account Manager Access</div>
              <div style={{ fontSize:11, color:'#a0b4c0' }}>Logging into <strong style={{ color:'#c8d6e0' }}>{impersonateOrg.name}</strong></div>
            </div>
          </div>
        )}
        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:36 }}>
          {brandLogo ? (
            <img src={brandLogo} alt="Logo"
              style={{ height:64, maxWidth:240, objectFit:'contain', display:'block', margin:'0 auto 16px' }}
              onError={e => e.target.style.display='none'}/>
          ) : (
            <>
              <div style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:60, height:60, borderRadius:16, marginBottom:16, background:'#00e5a018', border:'1.5px solid #00e5a030', fontSize:28 }}>📡</div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:26, color:'#e8edf2' }}>
                {platformName}
              </div>
            </>
          )}
          <div style={{ fontSize:12, color:'#7090a0', marginTop:6 }}>Warehouse Management System</div>
        </div>

        {/* Card */}
        <div style={{ background:'#0d1520', border:'1px solid #1a2a3a', borderRadius:14, padding:28 }}>
          <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div>
              <div style={{ fontSize:11, color:'#7090a0', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:6 }}>Email</div>
              <input className="inp" type="email" placeholder="admin@company.com" value={email} onChange={e=>setEmail(e.target.value)} autoFocus/>
            </div>
            <div>
              <div style={{ fontSize:11, color:'#7090a0', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:6 }}>Password</div>
              <input className="inp" type="password" placeholder="••••••••" value={password} onChange={e=>setPassword(e.target.value)}/>
            </div>

            {error && (
              <div style={{ background:'#e0575718', border:'1px solid #e0575730', borderRadius:8, padding:'10px 14px', fontSize:12, color:'#e05757' }}>
                {error}
              </div>
            )}

            <button type="submit" disabled={loading||!email||!password} className="btn"
              style={{ background:'#00e5a0', color:'#060b10', width:'100%', padding:'12px 0', fontSize:14, marginTop:4, opacity:(loading||!email||!password)?0.5:1 }}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          {/* Divider */}
          <div style={{ display:'flex', alignItems:'center', gap:10, margin:'20px 0' }}>
            <div style={{ flex:1, height:1, background:'#1a2a3a' }}/>
            <span style={{ fontSize:11, color:'#7090a0' }}>New here?</span>
            <div style={{ flex:1, height:1, background:'#1a2a3a' }}/>
          </div>

          {/* Register button */}
          <button onClick={() => setShowReg(true)}
            style={{ width:'100%', padding:'11px 0', borderRadius:8, border:'1px solid #1a2a3a', background:'none', color:'#c8d6e0', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit', transition:'border-color .15s' }}
            onMouseEnter={e=>e.currentTarget.style.borderColor='#00e5a040'}
            onMouseLeave={e=>e.currentTarget.style.borderColor='#1a2a3a'}>
            🔑 Register with Invite Code
          </button>
        </div>

        <div style={{ textAlign:'center', marginTop:20, fontSize:11, color:'#7090a0' }}>
          {platformName} · Warehouse Management System
        </div>
      </div>
    </div>
  );
}
